/**
 * O atendimento do SISAC como a tela fala (#1151): o que é, quando foi
 * marcado e para quando.
 *
 * As datas vêm na hora da clínica, sem fuso (como o SISAC guarda): a tela
 * mostra o relógio como veio, sem converter para o fuso de quem olha.
 */

import type { AtendimentoDoSisac } from '@cliqcrm/contracts';

const ROTULO_DO_TIPO: Record<AtendimentoDoSisac['tipo'], string> = {
  consulta: 'Consulta',
  exame_agendado: 'Exame agendado',
  exame_atendido: 'Exame atendido',
  exame: 'Exame realizado',
  falta: 'Faltou ao exame',
};

export function rotuloDoTipo(tipo: AtendimentoDoSisac['tipo']): string {
  return ROTULO_DO_TIPO[tipo];
}

/**
 * O que foi feito: o serviço, e o tipo quando o serviço não diz mais que ele. O exame
 * da agenda só tem nome quando a recepção o escreveu na observação (`servico_da_observacao`).
 */
export function nomeDoAtendimento(atendimento: AtendimentoDoSisac): string {
  const tipo = rotuloDoTipo(atendimento.tipo);
  const servico = atendimento.servico;
  const daAgenda = atendimento.tipo === 'falta' || atendimento.tipo === 'exame_atendido';
  if (daAgenda && !(atendimento.servico_da_observacao && servico)) return tipo;
  if (atendimento.tipo === 'falta') return `${tipo} · ${servico}`;
  return servico ?? tipo;
}

/** O valor previsto (o exame marcado, pela tabela) não é cobrança: a tela diz isso. */
export function valorEhPrevisto(atendimento: AtendimentoDoSisac): boolean {
  return (
    atendimento.tipo === 'exame_agendado' &&
    atendimento.servico_da_observacao &&
    atendimento.valor !== null
  );
}

/** A explicação da marca "pela agenda", para o `title` e o leitor de tela. */
export const PELA_AGENDA = 'O exame vem do que a recepção anotou na agenda do SISAC.';

/** "29/10/2026 09:30", do relógio da clínica; sem hora, só o dia. */
export function quandoNaClinica(instante: string | null): string | null {
  const partes = instante?.match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (!partes) return null;
  const [, ano, mes, dia, hora, minuto] = partes;
  const data = `${dia}/${mes}/${ano}`;
  return hora && !(hora === '00' && minuto === '00') ? `${data} ${hora}:${minuto}` : data;
}

/** Só o dia, "27/10": o "Agendado em" do card, onde o espaço é curto. */
export function diaNaClinica(instante: string | null): string | null {
  const partes = instante?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return partes ? `${partes[3]}/${partes[2]}` : null;
}
